import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Container,
  Avatar,
  Card,
  CardContent,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Save, Cancel, Close } from '@mui/icons-material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { FaUserEdit } from "react-icons/fa";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { postData } from "../../Webservice/Webservice";

function Profile() {
  const [isEditing, setIsEditing] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingField, setEditingField] = useState(null); // 'email' or 'phone'
  const [editingValue, setEditingValue] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    gender: '',
    age: '',
    dob: '',
    mobile: '',
    addresses: [],
    devices: [],
    isVerified: false,
    status: '',
    createdAt: '',
    updatedAt: '',
  });
  const [loginType, setLoginType] = useState(''); // 'email' or 'phone'

  useEffect(() => {
    // Load user data from sessionStorage
    const userData = sessionStorage.getItem('user');
    if (userData) {
      const user = JSON.parse(userData);
      setProfileData({
        name: user.name || '',
        email: user.email || '',
        phone: user.mobile || '',
        gender: user.gender || '',
        age: user.age || '',
        dob: user.dob && !isNaN(new Date(user.dob).getTime()) ? new Date(user.dob).toISOString().split('T')[0] : '',
        mobile: user.mobile || '',
        addresses: user.addresses || [],
        devices: user.devices || [],
        isVerified: user.isVerified || false,
        status: user.status || '',
        createdAt: user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '',
        updatedAt: user.updatedAt ? new Date(user.updatedAt).toLocaleDateString() : '',
      });

      // Determine login type based on available data
      // If email exists and is the primary login method, set loginType to 'email'
      // If phone/mobile exists and is the primary login method, set loginType to 'phone'
      // You may need to adjust this logic based on your actual login system
      if (user.email && !user.loginType) {
        setLoginType('email');
      } else if (user.mobile && !user.loginType) {
        setLoginType('phone');
      } else {
        setLoginType(user.loginType || 'email'); // Default to email if not specified
      }
    }
  }, []);

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleInputChange = (field) => (event) => {
    setProfileData({
      ...profileData,
      [field]: event.target.value,
    });
  };

  const handleSave = async () => {
    // Basic validation
    if (!profileData.name || profileData.name.trim() === '') {
      alert('Name is required');
      return;
    }

    if (!profileData.gender || profileData.gender === '') {
      alert('Please select your gender');
      return;
    }

    setIsEditing(false);

    try {
      // Prepare data for API request
      const updateData = {
        dob: profileData.dob,
        name: profileData.name,
        gender: profileData.gender
      };

      // Get user token from sessionStorage
      const userData = sessionStorage.getItem('user');
      const user = userData ? JSON.parse(userData) : null;
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";

      // Make POST request using postData
      const result = await postData('profile/update', updateData, token);

      console.log('Profile updated successfully:', result);

      // Update sessionStorage with new data
      if (user) {
        const updatedUser = { ...user, ...updateData };
        sessionStorage.setItem('user', JSON.stringify(updatedUser));
      }

      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('An error occurred while updating your profile. Please try again.');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleOpenEditModal = (field) => {
    setEditingField(field);
    setEditingValue(profileData[field] || '');
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingField(null);
    setEditingValue('');
    setOtpSent(false);
    setOtpValue('');
  };

  const handleSaveEditModal = async () => {
    if (editingField) {
      try {
        // Prepare data for API request
        let updateData;
        let apiEndpoint;

        if (editingField === 'phone') {
          // Use OTP endpoint for phone number updates
          updateData = {
            type: 'mobile',
            value: editingValue
          };
          apiEndpoint = 'profile/update/otp';
        } else if (editingField === 'email') {
          // Use OTP endpoint for email updates
          updateData = {
            type: 'email',
            value: editingValue
          };
          apiEndpoint = 'profile/update/otp';
        } else {
          // Use regular update endpoint for other fields
          updateData = {};
          updateData[editingField] = editingValue;
          apiEndpoint = 'profile/update';
        }

        // Get user token from sessionStorage
        const tokenValue = sessionStorage.getItem("token");
        const token = tokenValue ? `Bearer ${tokenValue}` : "";

        // Make POST request using postData
        const result = await postData(apiEndpoint, updateData, token);

        console.log(`${editingField} updated successfully:`, result);

        if (editingField === 'phone' || editingField === 'email') {
          // For phone number and email, show OTP input field
          setOtpSent(true);
          alert('OTP sent successfully!');
        } else {
          // For other fields, directly update and close modal
          setProfileData({
            ...profileData,
            [editingField]: editingValue,
          });

          // Update sessionStorage with new data
          const userData = sessionStorage.getItem('user');
          const user = userData ? JSON.parse(userData) : null;
          if (user) {
            const updatedUser = { ...user, ...updateData };
            sessionStorage.setItem('user', JSON.stringify(updatedUser));
          }

          alert(`${editingField} updated successfully!`);
          handleCloseEditModal();
        }
      } catch (error) {
        console.error(`Error updating ${editingField}:`, error);
        alert(`An error occurred while updating your ${editingField}. Please try again.`);
        return; // Don't close modal if update failed
      }
    }
  };

  const handleVerifyOtp = async () => {
    if ((editingField === 'phone' || editingField === 'email') && otpValue) {
      try {
        // Prepare OTP verification data
        const verifyData = {
          type: editingField === 'phone' ? 'mobile' : 'email',
          otp: otpValue
        };

        // Get user token from sessionStorage
        const tokenValue = sessionStorage.getItem("token");
        const token = tokenValue ? `Bearer ${tokenValue}` : "";

        // Make POST request to verify OTP
        const result = await postData('profile/update/verifyotp', verifyData, token);

        console.log('OTP verified successfully:', result);

        // Update local state
        setProfileData({
          ...profileData,
          [editingField]: editingValue,
        });

        // Update sessionStorage with new data
        const userData = sessionStorage.getItem('user');
        const user = userData ? JSON.parse(userData) : null;
        if (user) {
          const updatedUser = editingField === 'phone'
            ? { ...user, mobile: editingValue }
            : { ...user, email: editingValue };
          sessionStorage.setItem('user', JSON.stringify(updatedUser));
        }

        alert(`${editingField === 'phone' ? 'Phone number' : 'Email'} updated successfully!`);
        handleCloseEditModal();
      } catch (error) {
        console.error('Error verifying OTP:', error);
        alert('Invalid OTP. Please try again.');
      }
    }
  };

  const handleOpenPasswordModal = () => {
    setPasswordData({
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setPasswordModalOpen(true);
  };

  const handleClosePasswordModal = () => {
    setPasswordModalOpen(false);
    setPasswordData({
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
  };

  const handlePasswordChange = (field) => (event) => {
    setPasswordData({
      ...passwordData,
      [field]: event.target.value,
    });
  };

  const handleSavePassword = async () => {
    // Validate passwords match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New passwords do not match');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      alert('New password must be at least 6 characters');
      return;
    }

    try {
      // Prepare data for API request
      const passwordUpdateData = {
        oldPassword: passwordData.oldPassword,
        newPassword: passwordData.newPassword
      };

      // Get user token from sessionStorage
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";

      // Make POST request using postData
      const result = await postData('profile/update/password', passwordUpdateData, token);

      console.log('Password updated successfully:', result);

      handleClosePasswordModal();
      alert('Password updated successfully!');
    } catch (error) {
      console.error('Error updating password:', error);
      alert('An error occurred while updating your password. Please try again.');
    }
  };

  return (
    <Box sx={{
      py: { xs: 3, md: 5 },
      backgroundImage: 'url(https://t4.ftcdn.net/jpg/01/94/83/35/360_F_194833567_g3SapyrnYDmoasuEyHuKI0gS3NVcUvj1.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    }}>
      <Container maxWidth="lg"
        sx={{ py: 4, }}>
        <Paper
          elevation={3}
          sx={{
            p: 4,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
            {/* Profile Image Section */}
            <Card
              sx={{
                minWidth: { xs: "100%", md: 480 },
                maxWidth: { xs: "100%", md: 520 },
                borderRadius: 3,
                boxShadow: "none",
                background: "#F6F4EF",
              }}
            >
              <CardContent sx={{ textAlign: "center", p: 3 }}>
                <Avatar
                  src="/img/user.jpg"
                  alt="profile"
                  sx={{
                    width: { xs: 140, sm: 180, md: 250 },
                    height: { xs: 140, sm: 180, md: 250 },
                    mx: "auto",
                    mb: 2,
                    border: "4px solid",
                    borderColor: "divider",
                  }}
                />

                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  {profileData.name}
                </Typography>

                {/* Email Row */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Typography variant="body1" sx={{ color: "text.secondary" }}>
                    {profileData.email || 'Email Not Provided'}
                  </Typography>
                  {loginType !== 'email' && (
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEditModal('email')}
                      sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                {/* Phone Row */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Typography variant="body1" sx={{ color: "text.secondary" }}>
                    {profileData.phone || 'Phone Not Provided'}
                  </Typography>
                  {loginType !== 'phone' && (
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEditModal('phone')}
                      sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                {/* Age */}
                {loginType !== 'phone' && (
                  <Button
                    onClick={handleOpenPasswordModal}
                    sx={{ color: "black", mb: 1, cursor: 'pointer', border: '1px solid black', borderRadius: 2, px: 2, py: 0.5, fontWeight: 600 }}>
                    Update password
                  </Button>
                )}
              </CardContent>
            </Card>


            {/* Profile Details Section */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ mb: 4, mt: 2 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    mb: 1,
                  }}
                >
                  Profile Information
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Update your personal information and contact details
                </Typography>
              </Box>

              <Divider sx={{ mb: 4 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={profileData.name}
                    onChange={handleInputChange('name')}
                    disabled={!isEditing}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: 'black',
                        },
                      },
                    }}
                  />
                </Grid>



                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Gender"
                    value={profileData.gender}
                    onChange={handleInputChange('gender')}
                    disabled={!isEditing}
                    variant="outlined"
                    SelectProps={{ native: true }}
                    sx={{
                      width: '220px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: 'black',
                        },
                      },
                    }}
                  >
                    <option value="" disabled>Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </TextField>
                </Grid>



                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Date of Birth"
                    type="date"
                    value={profileData.dob}
                    onChange={handleInputChange('dob')}
                    disabled={!isEditing}
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: 'black',
                        },
                      },
                    }}
                  />
                </Grid>




                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Account Status"
                    value={profileData.status}
                    disabled={true}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: 'black',
                        },
                      },
                    }}
                  />
                </Grid>




              </Grid>


              {/* Action Buttons */}
              {!isEditing && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'flex-end',
                    mt: 12.5,
                    pt: 2.8,
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleEditToggle}
                    startIcon={<FaUserEdit />}
                    sx={{
                      width: { xs: '90%', sm: '60%', md: '50%' },
                      bgcolor: 'black',
                      color: 'white',
                      fontWeight: 600,
                      py: 1.5,
                      borderRadius: 2,
                      '&:hover': { bgcolor: 'black', boxShadow: 3, transform: 'translateY(-2px)' },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    Edit Profile
                  </Button>
                </Box>
              )}
              {isEditing && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'flex-end',
                    mt: 12.5,
                    pt: 2.8,
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<Cancel />}
                    onClick={handleCancel}
                    sx={{
                      borderColor: 'grey.400',
                      color: 'text.primary',
                      px: { sm: 4 },
                      py: 1.2,
                      borderRadius: 2,
                      fontWeight: 600,
                      width: { xs: '100%', sm: 'auto' },
                      '&:hover': { borderColor: 'grey.600', bgcolor: 'grey.50' },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleSave}
                    sx={{
                      bgcolor: 'black',
                      color: 'white',
                      px: { sm: 4 },
                      py: 1.2,
                      borderRadius: 2,
                      fontWeight: 600,
                      width: { xs: '100%', sm: 'auto' },
                      '&:hover': { bgcolor: 'black', boxShadow: 3 },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    Save Changes
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* Edit Modal Dialog */}
      <Dialog
        open={editModalOpen}
        onClose={handleCloseEditModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Update {editingField === 'email' ? 'Email' : 'Phone Number'}
          <IconButton size="small" onClick={handleCloseEditModal}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {!otpSent ? (
            <TextField
              fullWidth
              label={editingField === 'email' ? 'Email Address' : 'Phone Number'}
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              variant="outlined"
              type={editingField === 'email' ? 'email' : 'tel'}
              sx={{
                mt: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: 'black',
                  },
                },
              }}
            />
          ) : (
            <Box>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Enter the 6-digit OTP sent to {editingValue}
              </Typography>
              <TextField
                fullWidth
                label="OTP"
                value={otpValue}
                onChange={(e) => setOtpValue(e.target.value)}
                variant="outlined"
                inputProps={{ maxLength: 6, pattern: '[0-9]*' }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: 'black',
                    },
                  },
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleCloseEditModal}
            sx={{
              borderColor: 'grey.400',
              color: 'text.primary',
              borderRadius: 1,
              '&:hover': { borderColor: 'grey.600', bgcolor: 'grey.50' },
            }}
          >
            Cancel
          </Button>
          {!otpSent ? (
            <Button
              variant="contained"
              onClick={handleSaveEditModal}
              sx={{
                bgcolor: 'black',
                color: 'white',
                borderRadius: 1,
                '&:hover': { bgcolor: '#333' },
              }}
            >
              Send OTP
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleVerifyOtp}
              sx={{
                bgcolor: 'black',
                color: 'white',
                borderRadius: 1,
                '&:hover': { bgcolor: '#333' },
              }}
            >
              Verify
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Password Change Modal Dialog */}
      <Dialog
        open={passwordModalOpen}
        onClose={handleClosePasswordModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Update Password
          <IconButton size="small" onClick={handleClosePasswordModal}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Old Password"
            type={showOldPassword ? 'text' : 'password'}
            value={passwordData.oldPassword}
            onChange={handlePasswordChange('oldPassword')}
            variant="outlined"
            sx={{
              mt: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: 'black',
                },
              },
            }}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => setShowOldPassword(!showOldPassword)}
                  edge="end"
                  sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
                >
                  {showOldPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            }}
          />
          <TextField
            fullWidth
            label="New Password"
            type={showNewPassword ? 'text' : 'password'}
            value={passwordData.newPassword}
            onChange={handlePasswordChange('newPassword')}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: 'black',
                },
              },
            }}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  edge="end"
                  sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
                >
                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Confirm New Password"
            type={showConfirmPassword ? 'text' : 'password'}
            value={passwordData.confirmPassword}
            onChange={handlePasswordChange('confirmPassword')}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: 'black',
                },
              },
            }}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                  sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleClosePasswordModal}
            sx={{
              borderColor: 'grey.400',
              color: 'text.primary',
              borderRadius: 1,
              '&:hover': { borderColor: 'grey.600', bgcolor: 'grey.50' },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSavePassword}
            sx={{
              bgcolor: 'black',
              color: 'white',
              borderRadius: 1,
              '&:hover': { bgcolor: '#333' },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Profile;